import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import DOMPurify from 'dompurify';
import axios from 'axios';
import { Button } from '../common'; // Import Button
import ConfirmationModal from '../common/ConfirmationModal'; // Import ConfirmationModal
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { parseEmailContent } from '../../utils/emailParser';

const EmailModal = ({ isOpen, onClose, lead, replyToEmail = null }) => {
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);
  const messageRef = useRef(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkData, setLinkData] = useState({ text: '', url: '' });
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Notes History State
  const [notesHistory, setNotesHistory] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [notesError, setNotesError] = useState('');
  const [notesCollapsed, setNotesCollapsed] = useState(true); // Start with notes collapsed
  
  // Helper function to generate reply subject
  const generateReplySubject = (originalSubject) => {
    if (!originalSubject) return 'Re: ';
    if (originalSubject.toLowerCase().startsWith('re:')) {
      return originalSubject;
    }
    return `Re: ${originalSubject}`;
  };

  // Helper function to generate reply message with quoted original
  const generateReplyMessage = (originalEmail) => {
    if (!originalEmail) return '';
    
    const originalDate = new Date(originalEmail.created_at).toLocaleString();
    const originalSender = originalEmail.sender_name || 'Unknown';
    
    // Parse the original message content using the existing email parser
    let originalContent = '';
    if (originalEmail.message_body) {
      try {
        const { content, isHtml } = parseEmailContent(originalEmail.message_body);
        if (content) {
          if (isHtml) {
            // Convert HTML to text while preserving line breaks
            originalContent = content
              .replace(/<br\s*\/?>/gi, '\n') // Convert <br> tags to line breaks
              .replace(/<\/p>/gi, '\n\n') // Convert closing </p> tags to double line breaks
              .replace(/<p[^>]*>/gi, '') // Remove opening <p> tags
              .replace(/<\/div>/gi, '\n') // Convert closing </div> tags to line breaks
              .replace(/<div[^>]*>/gi, '') // Remove opening <div> tags
              .replace(/<\/h[1-6]>/gi, '\n\n') // Convert heading closing tags to double line breaks
              .replace(/<h[1-6][^>]*>/gi, '') // Remove heading opening tags
              .replace(/<\/li>/gi, '\n') // Convert list item closing tags to line breaks
              .replace(/<li[^>]*>/gi, '• ') // Convert list items to bullet points
              .replace(/<[^>]*>/g, '') // Remove any remaining HTML tags
              .replace(/&nbsp;/g, ' ') // Replace &nbsp; with spaces
              .replace(/&amp;/g, '&') // Replace &amp; with &
              .replace(/&lt;/g, '<') // Replace &lt; with <
              .replace(/&gt;/g, '>') // Replace &gt; with >
              .replace(/&quot;/g, '"') // Replace &quot; with "
              .replace(/[ \t]+/g, ' ') // Normalize spaces and tabs but preserve line breaks
              .replace(/\n\s*\n\s*\n/g, '\n\n') // Normalize multiple line breaks to double line breaks
              .replace(/^\s+|\s+$/g, '') // Trim whitespace from start and end
              .trim();
          } else {
            originalContent = content.trim();
          }
        } else {
          originalContent = 'Could not parse original message content.';
        }
      } catch (e) {
        console.error('Error parsing original email for reply:', e);
        originalContent = 'Error parsing original message content.';
      }
    }
    
    // Format the message with HTML line breaks for ReactQuill (rich text mode)
    const formattedMessage = `<br><br>------- Original Message -------<br>From: ${originalSender}<br>Date: ${originalDate}<br>Subject: ${originalEmail.subject || '(no subject)'}<br><br>${originalContent.replace(/\n/g, '<br>')}`;
    
    return formattedMessage;
  };

  const [emailData, setEmailData] = useState({
    to: lead?.email || '',
    cc: '',
    bcc: '',
    subject: replyToEmail ? generateReplySubject(replyToEmail.subject) : '',
    message: replyToEmail ? generateReplyMessage(replyToEmail) : '',
    loading: false,
    error: null,
    success: false
  });
  
  // Scheduling state
  const [scheduleEmail, setScheduleEmail] = useState(false);
  const [scheduledDateTime, setScheduledDateTime] = useState(new Date());
  
  // State to track which email is selected as primary recipient
  const [useSecondaryAsTo, setUseSecondaryAsTo] = useState(false);
  
  // AI states
  const [showAiGenerator, setShowAiGenerator] = useState(false);
  const [showAiRefiner, setShowAiRefiner] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiRefining, setAiRefining] = useState(false);
  const [includeCompanyInfo, setIncludeCompanyInfo] = useState(true);
  const [aiPromptData, setAiPromptData] = useState({
    topic: '',
    audience: '',
    goal: '',
    tone: 'professional',
    language: 'english',
    additionalInfo: ''
  });
  const [aiRefinementInstructions, setAiRefinementInstructions] = useState('');

  // Rich text editor configuration
  const modules = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline'],
        [{'list': 'ordered'}, {'list': 'bullet'}],
        ['link', 'clean'],
        [{'color': []}, {'background': []}],
        [{'size': ['small', false, 'large']}]
      ],
    }
  };
  
  const formats = [
    'bold', 'italic', 'underline',
    'list', 'bullet',
    'link',
    'color', 'background',
    'size'
  ];
  
  const [htmlMode, setHtmlMode] = useState(false);
  const [previewMode, setPreviewMode] = useState(false); // New state for HTML email preview
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [templateVarsCollapsed, setTemplateVarsCollapsed] = useState(true); // Start with template variables collapsed
  const quillRef = useRef(null); // Reference to ReactQuill component
  
  // Check if there's unsaved content
  const hasUnsavedContent = () => {
    // Check if there's any content in the message or subject fields
    return emailData.message.trim() !== '' ||
           emailData.subject.trim() !== '' ||
           attachments.length > 0 ||
           emailData.cc.trim() !== '' ||
           emailData.bcc.trim() !== '' ||
           scheduleEmail;
  };

  // Safe close with confirmation
  const handleSafeClose = () => {
    if (hasUnsavedContent()) {
      setShowConfirmModal(true);
    } else {
      onClose();
    }
  };

  // Handle key press
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.preventDefault(); // Prevent default browser behavior
      e.stopPropagation(); // Stop event from reaching parent modals
      handleSafeClose();
    }
  }, [onClose]);
  
  // Fetch templates and notes when modal opens
  useEffect(() => {
    if (isOpen && lead) { // Ensure lead is available
      fetchTemplates();
      fetchNotesHistory(); // Fetch notes
      document.addEventListener('keydown', handleKeyDown, true); // Use capturing phase
      
      // Reset the form state when opening modal
      setEmailData({
        to: lead?.email || '',
        cc: '',
        bcc: '',
        subject: replyToEmail ? generateReplySubject(replyToEmail.subject) : '',
        message: replyToEmail ? generateReplyMessage(replyToEmail) : '',
        loading: false,
        error: null,
        success: false
      });
      
      // Reset email selection state
      setUseSecondaryAsTo(false);
      setAttachments([]);
      setHtmlMode(false); // Start in Preview mode by default
      setShowCc(false); // Hide CC field by default
      setShowBcc(false); // Hide BCC field by default
      
      // Reset scheduling state
      setScheduleEmail(false);
      setScheduledDateTime(new Date());
    }
    
    // Clean up
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true); // Use capturing phase
      // Clean up any object URLs to prevent memory leaks
      attachments.forEach(attachment => {
        if (attachment.preview) {
          URL.revokeObjectURL(attachment.preview);
        }
      });
      // Reset notes state on close
      setNotesHistory([]);
      setLoadingNotes(false);
      setNotesError('');
    };
  }, [isOpen, handleKeyDown, lead, replyToEmail]); // Add replyToEmail to dependency array
  
  // No need for additional synchronization with our new approach
  // The textarea is always controlled by the React state
  
  // Fetch email templates
  const fetchTemplates = async () => {
    try {
      setLoadingTemplates(true);
      
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const res = await axios.get('/api/email/templates', config);
      
      setTemplates(res.data);
      
      // Don't automatically apply any template
      setSelectedTemplateId('');
      
      setLoadingTemplates(false);
    } catch (err) {
      console.error('Error fetching email templates:', err);
      setLoadingTemplates(false);
    }
  };
  
  // Apply selected template with variable replacements
  const applyTemplate = (template) => {
    if (!template) return;
    
    // Replace variables in subject and body
    let subject = template.subject || '';
    let body = template.body || '';
    
    // Perform variable replacements
    // Note: We've switched from replacing variables here to preserving them in the template
    // because the rich text editor now handles them with styling
    
    // Update email data with template content
    setEmailData(prev => ({
      ...prev,
      subject,
      message: body
    }));
    
    // Set mode to HTML if needed to see raw HTML
    if (htmlMode) {
      setHtmlMode(false);
    }
  };
  
  // Handle template selection change
  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    setSelectedTemplateId(templateId);
    
    // Apply the selected template if one is selected
    if (templateId) {
      const selectedTemplate = templates.find(t => t.id === parseInt(templateId));
      if (selectedTemplate) {
        applyTemplate(selectedTemplate);
      }
    }
  };

  // Fetch notes history for selected lead
  const fetchNotesHistory = async () => {
    if (!lead || !lead.id) return;
    
    try {
      setLoadingNotes(true);
      setNotesError('');
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const res = await axios.get(`/api/leads/${lead.id}/notes-history`, config);
      if (res.data && Array.isArray(res.data)) {
        setNotesHistory(res.data);
      }
    } catch (err) {
      console.error('Error fetching notes history:', err);
      setNotesError('Failed to load notes history.');
      setNotesHistory([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  // Format date for notes timestamp
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (e) {
      console.error("Error formatting date:", e);
      return dateString; // Return original string if formatting fails
    }
  };
  
  if (!isOpen) return null;
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setEmailData(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle paste event to preserve formatting
  const handlePaste = (e) => {
    try {
      e.preventDefault();
      
      // Get clipboard data
      const clipboardData = e.clipboardData || window.clipboardData;
      if (!clipboardData) return;
      
      let pastedData = '';
      
      // Try to get HTML content first
      if (clipboardData.types && clipboardData.types.includes('text/html')) {
        pastedData = clipboardData.getData('text/html');
        
        // Extract the body content if it's a full HTML document
        if (pastedData.includes('<html') && pastedData.includes('<body')) {
          const bodyMatch = pastedData.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
          if (bodyMatch && bodyMatch[1]) {
            pastedData = bodyMatch[1];
          }
        }
        
        // Sanitize HTML content before inserting
        pastedData = DOMPurify.sanitize(pastedData, {
          ALLOWED_TAGS: ['a', 'b', 'strong', 'i', 'em', 'u', 'p', 'br', 'ul', 'ol', 'li', 'span', 'div'],
          ALLOWED_ATTR: ['href', 'target', 'style', 'rel']
        });
        
        // Clean up styles to ensure consistent appearance
        pastedData = pastedData
          .replace(/<p>\s*<\/p>/g, '<br>') // Empty paragraphs to line breaks
          .replace(/<div>\s*<\/div>/g, '<br>'); // Empty divs to line breaks
      } else {
        // Fall back to plain text if no HTML is available
        pastedData = clipboardData.getData('text/plain');
        
        // We don't convert newlines to <br> here because we want them
        // to show as actual newlines in the textarea
      }
      
      // Insert in textarea
      const textarea = messageRef.current;
      if (!textarea) return;
      
      // Get cursor position
      const startPos = textarea.selectionStart;
      const endPos = textarea.selectionEnd;
      
      // Insert the cleaned HTML at cursor position
      const textBeforeCursor = emailData.message.substring(0, startPos);
      const textAfterCursor = emailData.message.substring(endPos);
      const newMessage = textBeforeCursor + pastedData + textAfterCursor;
      
      // Update state
      setEmailData(prev => ({ ...prev, message: newMessage }));
      
      // Set cursor position after the pasted content
      setTimeout(() => {
        textarea.focus();
        const newPosition = startPos + pastedData.length;
        textarea.selectionStart = newPosition;
        textarea.selectionEnd = newPosition;
      }, 0);
      
    } catch (error) {
      console.error("Error handling paste:", error);
      
      // Fallback - let the browser handle the paste normally
      e.preventDefault = function() {};
    }
  };
  
  // Handle file selection
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Create file previews
    const newAttachments = files.map(file => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      // Create a local URL for preview if it's an image
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));
    
    setAttachments(prev => [...prev, ...newAttachments]);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Handle removing an attachment
  const handleRemoveAttachment = (index) => {
    setAttachments(prev => {
      const newAttachments = [...prev];
      // Revoke object URL if it exists to prevent memory leaks
      if (newAttachments[index].preview) {
        URL.revokeObjectURL(newAttachments[index].preview);
      }
      newAttachments.splice(index, 1);
      return newAttachments;
    });
  };
  
  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Apply template variables to preview content
  const getPreviewContent = (content, lead) => {
    if (!content || !lead) return content;

    let previewContent = content;
    previewContent = previewContent.replace(/{{lead_name}}/g, lead.name || '');
    previewContent = previewContent.replace(/{{lead_email}}/g, lead.email || '');
    previewContent = previewContent.replace(/{{country}}/g, lead.country || '');
    previewContent = previewContent.replace(/{{first_name}}/g, lead.first_name || lead.name?.split(' ')[0] || '');
    previewContent = previewContent.replace(/{{user_name}}/g, 'Your Sales Rep'); // Placeholder
    previewContent = previewContent.replace(/{{company_name}}/g, 'Your Company'); // Placeholder

    return previewContent;
  };

  // Generate email content using AI
  const generateEmailWithAI = async () => {
    try {
      setAiGenerating(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post('/api/bulk-mailer/generate-email', {
        topic: aiPromptData.topic,
        audience: aiPromptData.audience,
        goal: aiPromptData.goal,
        tone: aiPromptData.tone,
        language: aiPromptData.language,
        additionalInfo: aiPromptData.additionalInfo,
        includeCompanyInfo: includeCompanyInfo
      }, {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data && response.data.html) {
        // Switch to HTML preview mode to show the full email template
        setHtmlMode(false);
        setPreviewMode(true);
        setEmailData(prev => ({ 
          ...prev, 
          message: response.data.html,
          // Also update subject if provided
          subject: response.data.subject || prev.subject
        }));
        setShowAiGenerator(false);
        // Reset form
        setAiPromptData({
          topic: '',
          audience: '',
          goal: '',
          tone: 'professional',
          language: 'english',
          additionalInfo: ''
        });
      } else {
        throw new Error('Generated content is empty or invalid');
      }
    } catch (error) {
      console.error('Error generating email with AI:', error);
      setEmailData(prev => ({ 
        ...prev, 
        error: error.response?.data?.message || 'Failed to generate email content. Please try again.' 
      }));
    } finally {
      setAiGenerating(false);
    }
  };

  // Refine email content using AI
  const refineEmailWithAI = async () => {
    try {
      if (!aiRefinementInstructions) {
        setEmailData(prev => ({ ...prev, error: 'Please provide refinement instructions' }));
        return;
      }
      
      setAiRefining(true);
      setShowAiRefiner(false);
      
      const token = localStorage.getItem('token');
      
      const response = await axios.post('/api/bulk-mailer/refine-email', {
        currentContent: emailData.message,
        refinementInstructions: aiRefinementInstructions,
        includeCompanyInfo: includeCompanyInfo
      }, {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data && response.data.html) {
        // Switch to HTML preview mode to show the refined email template
        setHtmlMode(false);
        setPreviewMode(true);
        setEmailData(prev => ({ 
          ...prev, 
          message: response.data.html,
          // Also update subject if provided
          subject: response.data.subject || prev.subject
        }));
        setAiRefinementInstructions('');
      } else {
        throw new Error('Refined content is empty or invalid');
      }
    } catch (error) {
      console.error('Error refining email with AI:', error);
      setEmailData(prev => ({ 
        ...prev, 
        error: error.response?.data?.message || 'Failed to refine email content. Please try again.' 
      }));
    } finally {
      setAiRefining(false);
      setShowAiRefiner(true);
    }
  };
  
  // Apply formatting (bold, italic, underline)
  const applyFormatting = (type) => {
    // Always use textarea mode
    const textarea = messageRef.current;
    if (!textarea) return;
    
    // Get cursor position and selected text
    const startPos = textarea.selectionStart;
    const endPos = textarea.selectionEnd;
    const selectedText = textarea.value.substring(startPos, endPos) || '';
    
    // Determine HTML tags based on formatting type
    let openTag, closeTag;
    switch (type) {
      case 'bold':
        openTag = '<strong>';
        closeTag = '</strong>';
        break;
      case 'italic':
        openTag = '<em>';
        closeTag = '</em>';
        break;
      case 'underline':
        openTag = '<u>';
        closeTag = '</u>';
        break;
      default:
        return;
    }
    
    // If no text is selected, insert empty tags and place cursor between them
    if (selectedText === '') {
      const newMessage = 
        emailData.message.substring(0, startPos) + 
        openTag + closeTag + 
        emailData.message.substring(endPos);
      
      // Update state
      setEmailData(prev => ({ ...prev, message: newMessage }));
      
      // Move cursor between tags
      setTimeout(() => {
        textarea.focus();
        textarea.selectionStart = startPos + openTag.length;
        textarea.selectionEnd = startPos + openTag.length;
      }, 0);
    } else {
      // Wrap the selected text with formatting tags
      const newMessage = 
        emailData.message.substring(0, startPos) + 
        openTag + selectedText + closeTag + 
        emailData.message.substring(endPos);
      
      // Update state
      setEmailData(prev => ({ ...prev, message: newMessage }));
      
      // Maintain selection including tags
      setTimeout(() => {
        textarea.focus();
        textarea.selectionStart = startPos;
        textarea.selectionEnd = startPos + openTag.length + selectedText.length + closeTag.length;
      }, 0);
    }
  };

  // Handle link modal state
  const openLinkModal = () => {
    // Get selected text from textarea
    const textarea = messageRef.current;
    let selectedText = '';
    
    if (textarea) {
      selectedText = textarea.value.substring(
        textarea.selectionStart, 
        textarea.selectionEnd
      ) || '';
    }
    
    setLinkData({
      text: selectedText || 'Click here',
      url: 'https://'
    });
    
    setShowLinkModal(true);
  };
  
  const closeLinkModal = () => {
    setShowLinkModal(false);
  };
  
  const handleLinkDataChange = (e) => {
    const { name, value } = e.target;
    setLinkData(prev => ({ ...prev, [name]: value }));
  };
  
  const insertLink = () => {
    if (!linkData.text || !linkData.url) return;
    
    // Create HTML link tag
    const linkHtml = `<a href="${linkData.url}">${linkData.text}</a>`;
    
    // Insert in textarea
    const textarea = messageRef.current;
    if (!textarea) return;
    
    // Get cursor position
    const startPos = textarea.selectionStart;
    const endPos = textarea.selectionEnd;
    
    // Insert the link at cursor position or replace selected text
    const textBeforeCursor = emailData.message.substring(0, startPos);
    const textAfterCursor = emailData.message.substring(endPos);
    const newMessage = textBeforeCursor + linkHtml + textAfterCursor;
    
    // Update state
    setEmailData(prev => ({ ...prev, message: newMessage }));
    
    // Set focus back to textarea after inserting the link
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = startPos + linkHtml.length;
      textarea.selectionEnd = startPos + linkHtml.length;
    }, 0);
    
    // Close modal
    closeLinkModal();
  };
  
  // Schedule email for later
  const scheduleEmailForLater = async (leadToUse = lead) => {
    const token = localStorage.getItem('token');
    
    // Create text-only version by stripping HTML tags
    const textVersion = emailData.message.replace(/<[^>]*>?/gm, '');
    
    // Convert all newlines to <br> tags for proper HTML rendering
    const htmlVersion = emailData.message.replace(/\n/g, '<br>');
    
    return axios.post('/api/email/schedule', {
      to: emailData.to,
      cc: emailData.cc || undefined,
      bcc: emailData.bcc || undefined,
      subject: emailData.subject,
      text: textVersion,
      html: htmlVersion,
      scheduledFor: scheduledDateTime.toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      leadId: leadToUse.id
    }, {
      headers: {
        'Content-Type': 'application/json',
        'x-auth-token': token
      }
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate scheduled time if scheduling
    if (scheduleEmail) {
      const now = new Date();
      if (scheduledDateTime <= now) {
        setEmailData(prev => ({ 
          ...prev, 
          error: 'Scheduled time must be in the future' 
        }));
        return;
      }
    }
    
    try {
      setEmailData(prev => ({ ...prev, loading: true, error: null }));
      
      // If lead doesn't have an ID, create it first (for prospector emails)
      let currentLead = lead;
      if (!lead?.id && lead?._prospectorData) {
        try {
          // Create lead from prospector data
          const token = localStorage.getItem('token');
          const config = {
            headers: {
              'Content-Type': 'application/json',
              'x-auth-token': token
            }
          };

          const { member, company } = lead._prospectorData;
          
          // Determine country from company address or domain
          let country = '';
          if (company.forretningsadresse && company.forretningsadresse.land) {
            country = company.forretningsadresse.land;
          } else if (company.forretningsadresse && company.forretningsadresse.landkode) {
            const countryMap = {
              'NO': 'Norway',
              'SE': 'Sweden', 
              'DK': 'Denmark',
              'FI': 'Finland'
            };
            country = countryMap[company.forretningsadresse.landkode] || company.forretningsadresse.landkode;
          } else {
            country = 'Norway';
          }

          // Check if lead with this email already exists
          let existingLead = null;
          try {
            const searchResponse = await axios.get(`/api/leads?search=${encodeURIComponent(member.email)}`, config);
            console.log('Lead search response:', searchResponse.data);
            // Check if the response has leads array and it contains data
            const leads = searchResponse.data?.leads || searchResponse.data;
            if (leads && leads.length > 0) {
              existingLead = leads.find(searchLead => 
                searchLead.email?.toLowerCase() === member.email?.toLowerCase() || 
                searchLead.secondary_email?.toLowerCase() === member.email?.toLowerCase()
              );
              console.log('Found existing lead:', existingLead);
            } else {
              console.log('No existing leads found for email:', member.email);
            }
          } catch (searchErr) {
            console.log('Lead search failed, continuing with creation:', searchErr);
          }

          if (existingLead) {
            // Use existing lead
            currentLead = { ...lead, id: existingLead.id };
          } else {
            // Create new lead - truncate source to fit database constraints
            const sourceText = `Prospecting: ${company.navn}`;
            const truncatedSource = sourceText.length > 50 ? sourceText.substring(0, 47) + '...' : sourceText;
            
            const leadData = {
              name: member.name,
              first_name: lead.first_name,
              last_name: lead.last_name,
              position: member.position || '',
              role: member.position || '',
              company: company.navn,
              email: member.email || '',
              phone: member.phone || '',
              country: country,
              original_source: truncatedSource,
              latest_source: truncatedSource
            };

            const leadResponse = await axios.post('/api/leads', leadData, config);
            currentLead = { ...lead, id: leadResponse.data.id };
          }
        } catch (leadError) {
          console.error('Error creating lead:', leadError);
          setEmailData(prev => ({ 
            ...prev, 
            loading: false, 
            error: 'Failed to create lead. Please try again.' 
          }));
          return;
        }
      }
      
      if (scheduleEmail) {
        // Schedule the email
        await scheduleEmailForLater(currentLead);
      } else {
        // Send immediately
        // Check if we have attachments
        if (attachments.length > 0) {
          // Send with attachments using FormData
          await sendWithAttachments(currentLead);
        } else {
          // Send without attachments using regular JSON
          await sendWithoutAttachments(currentLead);
        }
      }
      
      // Clear attachments and revoke any object URLs
      attachments.forEach(attachment => {
        if (attachment.preview) {
          URL.revokeObjectURL(attachment.preview);
        }
      });
      setAttachments([]);
      
      setEmailData(prev => ({ 
        ...prev, 
        loading: false, 
        error: null,
        success: true 
      }));
      
      // Close the modal after 2 seconds and refresh activity
      setTimeout(() => {
        onClose(true); // Pass true to indicate success and refresh activity
      }, 2000);
      
    } catch (error) {
      console.error('Error sending email:', error);
      
      setEmailData(prev => ({ 
        ...prev, 
        loading: false, 
        error: error.response?.data?.message || 'Failed to send email. Please try again.' 
      }));
    }
  };
  
  // Send email with file attachments using FormData
  const sendWithAttachments = async (leadToUse = lead) => {
    const token = localStorage.getItem('token');
    
    // Create a fresh FormData instance
    const formData = new FormData();
    
    // Add text fields
    formData.append('to', emailData.to);
    if (emailData.cc) formData.append('cc', emailData.cc);
    if (emailData.bcc) formData.append('bcc', emailData.bcc);
    formData.append('subject', emailData.subject);
    
    // Create text-only version by stripping HTML tags
    const textVersion = emailData.message.replace(/<[^>]*>?/gm, '');
    formData.append('text', textVersion);
    
    // Convert all newlines to <br> tags for proper HTML rendering
    const htmlVersion = emailData.message.replace(/\n/g, '<br>');
    formData.append('html', htmlVersion);
    
    formData.append('leadId', leadToUse.id.toString());
    formData.append('from_ui', 'true');
    
    // Add each file
    for (let i = 0; i < attachments.length; i++) {
      const file = attachments[i].file;
      console.log(`Adding file: ${file.name}, type: ${file.type}, size: ${file.size}`);
      formData.append('attachments', file, file.name);
    }
    
    // Set headers but don't specify Content-Type - axios will set it with boundary
    const config = {
      headers: {
        'x-auth-token': token
      }
    };
    
    // Send the request
    return axios.post('/api/email/send-with-attachments', formData, config);
  };
  
  // Send email without attachments using regular JSON payload
  const sendWithoutAttachments = async (leadToUse = lead) => {
    const token = localStorage.getItem('token');
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'x-auth-token': token
      }
    };
    
    // Create text-only version by stripping HTML tags
    const textVersion = emailData.message.replace(/<[^>]*>?/gm, '');
    
    // Convert all newlines to <br> tags for proper HTML rendering
    const htmlVersion = emailData.message.replace(/\n/g, '<br>');
    
    return axios.post('/api/email/send', {
      to: emailData.to,
      cc: emailData.cc || undefined,
      bcc: emailData.bcc || undefined,
      subject: emailData.subject,
      text: textVersion,
      html: htmlVersion,
      leadId: leadToUse.id
    }, config);
  };

  return (
    <div 
      className="fixed inset-0 overflow-y-auto" 
      style={{zIndex: 100}}
      role="dialog" 
      aria-modal="true"
      onClick={(e) => e.stopPropagation()} // Prevent clicks from reaching parent modals
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !showLinkModal) {
          e.preventDefault();
          e.stopPropagation();
          handleSafeClose();
        }
      }}
    >
      <div className="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
          onClick={(e) => {
            e.stopPropagation(); // Stop event from reaching parent modals
            handleSafeClose();
          }}
        ></div>
        
        <div className="inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-w-3xl">
          <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  Send Email to {lead?.name}
                </h3>
                
                {emailData.error && (
                  <div className="mt-4 p-2 bg-red-50 text-red-700 border border-red-200 rounded">
                    {emailData.error}
                  </div>
                )}
                
                {emailData.success && (
                  <div className="mt-4 p-2 bg-green-50 text-green-700 border border-green-200 rounded flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Email sent successfully!
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="mt-4">
                  {/* Unified header row with To, Template, and Subject on same line */}
                  <div className="mb-4 flex space-x-4">
                    {/* To field */}
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <label htmlFor="to" className="block text-sm font-medium text-gray-700">
                          To
                        </label>
                        <div className="flex space-x-3">
                          <button 
                            type="button"
                            onClick={() => setShowCc(!showCc)}
                            className={`text-xs px-2 py-0.5 rounded-full transition-colors duration-200 flex items-center ${
                              showCc 
                                ? 'bg-blue-100 text-blue-700' 
                                : emailData.cc 
                                  ? 'bg-blue-50 text-blue-600 border border-blue-200' 
                                  : 'text-gray-500 hover:text-blue-600'
                            }`}
                          >
                            CC
                            {!showCc && emailData.cc && (
                              <span className="ml-1 w-2 h-2 bg-blue-500 rounded-full"></span>
                            )}
                          </button>
                          <button 
                            type="button"
                            onClick={() => setShowBcc(!showBcc)}
                            className={`text-xs px-2 py-0.5 rounded-full transition-colors duration-200 flex items-center ${
                              showBcc 
                                ? 'bg-blue-100 text-blue-700' 
                                : emailData.bcc 
                                  ? 'bg-blue-50 text-blue-600 border border-blue-200' 
                                  : 'text-gray-500 hover:text-blue-600'
                            }`}
                          >
                            BCC
                            {!showBcc && emailData.bcc && (
                              <span className="ml-1 w-2 h-2 bg-blue-500 rounded-full"></span>
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Email Selection */}
                      {lead?.secondary_email && (
                        <div className="mb-2">
                          <div className="flex space-x-2">
                            <button
                              type="button"
                              onClick={() => {
                                setUseSecondaryAsTo(false);
                                setEmailData(prev => ({
                                  ...prev,
                                  to: lead.email
                                }));
                              }}
                              className={`px-3 py-1 text-xs rounded-md transition-colors duration-200 flex-1 ${
                                !useSecondaryAsTo
                                  ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              Primary: {lead.email}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setUseSecondaryAsTo(true);
                                setEmailData(prev => ({
                                  ...prev,
                                  to: lead.secondary_email
                                }));
                              }}
                              className={`px-3 py-1 text-xs rounded-md transition-colors duration-200 flex-1 ${
                                useSecondaryAsTo
                                  ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              Secondary: {lead.secondary_email}
                            </button>
                          </div>
                        </div>
                      )}
                      
                      <input
                        type="email"
                        id="to"
                        name="to"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        value={emailData.to}
                        onChange={handleChange}
                        required
                        readOnly
                      />
                    </div>

                    {/* Template Selector */}
                    <div className="flex-1">
                      <label htmlFor="template" className="block text-sm font-medium text-gray-700 mb-1">
                        Template
                      </label>
                      <select
                        id="template"
                        name="template"
                        value={selectedTemplateId}
                        onChange={handleTemplateChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        disabled={loadingTemplates}
                      >
                        <option value="">-- Select a template --</option>
                        {templates.map(template => (
                          <option key={template.id} value={template.id}>
                            {template.name}
                          </option>
                        ))}
                      </select>
                      {loadingTemplates && (
                        <div className="text-xs text-gray-500 flex items-center">
                          <svg className="animate-spin h-3 w-3 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Loading...
                        </div>
                      )}
                    </div>

                    {/* Subject field */}
                    <div className="flex-1">
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                        Subject
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        value={emailData.subject}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  
                  {/* CC field - conditional rendering */}
                  {showCc && (
                    <div className="mb-4 animate-fadeIn">
                      <div className="flex justify-between items-center mb-1">
                        <label htmlFor="cc" className="block text-sm font-medium text-gray-700">
                          CC
                        </label>
                        {/* Add secondary email to CC button - only show when using primary as "To" */}
                        {lead?.secondary_email && !useSecondaryAsTo && (
                          <button
                            type="button"
                            onClick={() => {
                              // Add secondary email to CC (append it if CC already has content)
                              const currentCC = emailData.cc;
                              const newCC = currentCC 
                                ? `${currentCC}, ${lead.secondary_email}` 
                                : lead.secondary_email;
                              
                              setEmailData(prev => ({
                                ...prev,
                                cc: newCC
                              }));
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            + Add secondary email
                          </button>
                        )}
                        {/* Add primary email to CC button - only show when using secondary as "To" */}
                        {lead?.secondary_email && useSecondaryAsTo && (
                          <button
                            type="button"
                            onClick={() => {
                              // Add primary email to CC (append it if CC already has content)
                              const currentCC = emailData.cc;
                              const newCC = currentCC 
                                ? `${currentCC}, ${lead.email}` 
                                : lead.email;
                              
                              setEmailData(prev => ({
                                ...prev,
                                cc: newCC
                              }));
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            + Add primary email
                          </button>
                        )}
                      </div>
                      <input
                        type="text"
                        id="cc"
                        name="cc"
                        placeholder="email@example.com, another@example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        value={emailData.cc}
                        onChange={handleChange}
                        autoFocus
                      />
                      <p className="mt-1 text-xs text-gray-500">Separate multiple emails with commas</p>
                    </div>
                  )}
                  
                  {/* BCC field - conditional rendering */}
                  {showBcc && (
                    <div className="mb-4 animate-fadeIn">
                      <div className="flex justify-between items-center mb-1">
                        <label htmlFor="bcc" className="block text-sm font-medium text-gray-700">
                          BCC
                        </label>
                        {/* Add secondary email to BCC button - only show when using primary as "To" */}
                        {lead?.secondary_email && !useSecondaryAsTo && (
                          <button
                            type="button"
                            onClick={() => {
                              // Add secondary email to BCC (append it if BCC already has content)
                              const currentBCC = emailData.bcc;
                              const newBCC = currentBCC 
                                ? `${currentBCC}, ${lead.secondary_email}` 
                                : lead.secondary_email;
                              
                              setEmailData(prev => ({
                                ...prev,
                                bcc: newBCC
                              }));
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            + Add secondary email
                          </button>
                        )}
                        {/* Add primary email to BCC button - only show when using secondary as "To" */}
                        {lead?.secondary_email && useSecondaryAsTo && (
                          <button
                            type="button"
                            onClick={() => {
                              // Add primary email to BCC (append it if BCC already has content)
                              const currentBCC = emailData.bcc;
                              const newBCC = currentBCC 
                                ? `${currentBCC}, ${lead.email}` 
                                : lead.email;
                              
                              setEmailData(prev => ({
                                ...prev,
                                bcc: newBCC
                              }));
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            + Add primary email
                          </button>
                        )}
                      </div>
                      <input
                        type="text"
                        id="bcc"
                        name="bcc"
                        placeholder="email@example.com, another@example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        value={emailData.bcc}
                        onChange={handleChange}
                        autoFocus={showCc ? false : true}
                      />
                      <p className="mt-1 text-xs text-gray-500">Separate multiple emails with commas</p>
                    </div>
                  )}

                  {/* Notes History Section - Collapsible */}
                  <div className="mb-4 border border-gray-200 rounded-md bg-gray-50 overflow-hidden">
                    <button 
                      type="button"
                      onClick={() => setNotesCollapsed(!notesCollapsed)}
                      className="w-full p-3 text-left flex items-center justify-between focus:outline-none"
                    >
                      <div className="flex items-center">
                        <h4 className="text-sm font-medium text-gray-700">Notes History</h4>
                        {notesHistory.length > 0 && (
                          <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                            {notesHistory.length}
                          </span>
                        )}
                      </div>
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${notesCollapsed ? '' : 'transform rotate-180'}`} 
                        viewBox="0 0 20 20" 
                        fill="currentColor"
                      >
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                    
                    {/* Collapsible content */}
                    <div className={`px-3 pb-3 transition-all duration-200 ease-in-out ${notesCollapsed ? 'max-h-0 overflow-hidden opacity-0' : 'max-h-48 overflow-y-auto opacity-100'}`}>
                      {loadingNotes && <p className="text-xs text-gray-500">Loading notes...</p>}
                      {notesError && <p className="text-xs text-red-500">{notesError}</p>}
                      {!loadingNotes && !notesError && (
                        <div className="space-y-2 pr-2">
                          {notesHistory.length > 0 ? (
                            notesHistory.map((note) => (
                              <div key={note.id} className="bg-white p-2 rounded shadow-sm border border-gray-100">
                                <div className="flex justify-between items-center mb-1">
                                  <span className="text-xs font-medium text-gray-800">
                                    {note.user_name || 'Unknown User'}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {formatDate(note.timestamp)}
                                  </span>
                                </div>
                                <div className="text-sm text-gray-700 whitespace-pre-wrap">
                                  {note.content}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-sm text-gray-500 italic">No notes yet for this lead.</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-1">
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                        Message
                      </label>
                      <div className="flex items-center space-x-2">
                        {/* AI Buttons */}
                        <Button
                          type="button"
                          onClick={() => setShowAiGenerator(true)}
                          variant="primary"
                          className="!bg-indigo-600 hover:!bg-indigo-700 !text-xs px-2 py-1"
                          disabled={emailData.loading || emailData.success || aiGenerating}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                          </svg>
                          {aiGenerating ? 'Generating...' : 'Generate with AI'}
                        </Button>
                        
                        {emailData.message && emailData.message.trim() && (
                          <Button
                            type="button"
                            onClick={() => setShowAiRefiner(true)}
                            variant="primary"
                            className="!bg-indigo-600 hover:!bg-indigo-700 !text-xs px-2 py-1"
                            disabled={emailData.loading || emailData.success || aiRefining}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                            </svg>
                            {aiRefining ? 'Refining...' : 'Refine with AI'}
                          </Button>
                        )}
                        
                        {/* Mode Toggle */}
                        <div className="bg-gray-100 rounded-md p-0.5 flex items-center">
                          <button
                            type="button"
                            onClick={() => {
                              setHtmlMode(false);
                              setPreviewMode(false);
                            }}
                            className={`text-xs px-2 py-1 rounded-md ${!htmlMode && !previewMode ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-700'}`}
                          >
                            Rich Text
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setHtmlMode(false);
                              setPreviewMode(true);
                            }}
                            className={`text-xs px-2 py-1 rounded-md ${!htmlMode && previewMode ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-700'}`}
                          >
                            Email Preview
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setHtmlMode(true);
                              setPreviewMode(false);
                            }}
                            className={`text-xs px-2 py-1 rounded-md ${htmlMode ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-700'}`}
                          >
                            HTML Code
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Editing Area */}
                    <div className="relative">
                      {htmlMode ? (
                        // HTML Code Mode - for viewing/editing raw HTML
                        <textarea
                          id="message"
                          name="message"
                          rows={6}
                          ref={messageRef}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 font-mono"
                          style={{ minHeight: '120px', resize: 'vertical' }}
                          value={emailData.message}
                          onChange={handleChange}
                          required
                        ></textarea>
                      ) : previewMode ? (
                        // Email Preview Mode - for viewing full HTML email templates
                        <div className="border border-gray-300 rounded-md overflow-hidden bg-white">
                          <div className="p-4 border-b bg-gray-50">
                            <div className="text-sm font-medium text-gray-700 mb-1">Email Preview</div>
                            <div className="text-xs text-gray-500">This is how your email will appear to recipients</div>
                          </div>
                          <div 
                            className="p-4 overflow-auto"
                            style={{ minHeight: '200px', maxHeight: '500px' }}
                            dangerouslySetInnerHTML={{ 
                              __html: getPreviewContent(emailData.message, { 
                                name: lead?.name || 'John Doe', 
                                email: lead?.email || 'john@example.com', 
                                country: lead?.country || 'USA' 
                              }) 
                            }} 
                          />
                          <div className="p-2 border-t bg-gray-50 text-xs text-gray-500">
                            Switch to "Rich Text" mode to edit content manually, or "HTML Code" mode to edit raw HTML
                          </div>
                        </div>
                      ) : (
                        // Rich Text Mode - for manual editing with ReactQuill
                        <div className="border border-gray-300 rounded-md overflow-hidden">
                          <div className="editor-container">
                            <ReactQuill
                              ref={quillRef}
                              value={emailData.message}
                              onChange={(content) => setEmailData(prev => ({ ...prev, message: content }))}
                              modules={modules}
                              formats={formats}
                              placeholder="Type your message here..."
                              className="bg-white rounded-md"
                              theme="snow"
                            />
                            <style jsx>{`
                              /* Local styles for Quill editor */
                              .editor-container .ql-container {
                                font-size: 0.875rem;
                                font-family: inherit;
                                min-height: 120px;
                                height: auto;
                                max-height: 400px;
                                overflow-y: auto;
                              }
                              /* Make the editor grow automatically */
                              .editor-container .ql-editor {
                                min-height: 120px;
                                height: auto;
                              }
                              .editor-container .ql-toolbar {
                                background-color: #f9fafb;
                                border-bottom: 1px solid #e5e7eb;
                              }
                              .editor-container .ql-toolbar button {
                                color: #4b5563;
                              }
                              .editor-container .ql-toolbar button:hover {
                                color: #2563eb;
                              }
                              .editor-container .ql-toolbar button.ql-active,
                              .editor-container .ql-toolbar .ql-picker-label.ql-active {
                                color: #2563eb;
                              }
                              .editor-container .ql-toolbar .ql-picker-options {
                                background-color: white;
                                border-color: #e5e7eb;
                              }
                              .editor-container .ql-snow .ql-editor pre.ql-syntax {
                                font-family: monospace;
                                background-color: #f3f4f6;
                                color: #1f2937;
                              }
                            `}</style>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Variable dropdown for template-like functionality */}
                    {/* Combined variables and attachments row */}
                    <div className="flex items-center justify-between mt-2 mb-2">
                      {/* Variables section */}
                      <div className="flex items-center">
                        <div className="text-xs text-gray-700 mr-2">Insert variable:</div>
                        <div className="relative">
                          <select
                            className="block w-full pl-3 pr-10 py-1 text-xs border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md"
                            onChange={(e) => {
                              if (e.target.value && !htmlMode && quillRef.current) {
                                const variable = `{{${e.target.value}}}`;
                                const quill = quillRef.current.getEditor();
                                
                                // Store current selection before changing focus
                                const currentRange = quill.getSelection(true);
                                
                                // Insert at cursor position or at the end if no selection
                                if (currentRange) {
                                  quill.insertText(currentRange.index, variable);
                                } else {
                                  // If we don't have a selection, force focus first
                                  quill.focus();
                                  
                                  // Wait a bit for focus to register, then try to get selection again
                                  setTimeout(() => {
                                    const newRange = quill.getSelection(true);
                                    if (newRange) {
                                      // We got a selection after focusing
                                      quill.insertText(newRange.index, variable);
                                    } else {
                                      // Still no selection, insert at the end as fallback
                                      const length = quill.getText().length;
                                      quill.insertText(length > 0 ? length - 1 : 0, variable);
                                    }
                                  }, 10);
                                }
                              } else if (e.target.value && htmlMode) {
                                // Handle HTML mode
                                const variable = `{{${e.target.value}}}`;
                                const textarea = messageRef.current;
                                
                                if (textarea) {
                                  const startPos = textarea.selectionStart;
                                  const endPos = textarea.selectionEnd;
                                  const textBefore = emailData.message.substring(0, startPos);
                                  const textAfter = emailData.message.substring(endPos);
                                  
                                  // Update message with variable inserted at cursor position
                                  setEmailData(prev => ({
                                    ...prev,
                                    message: textBefore + variable + textAfter
                                  }));
                                  
                                  // Set cursor position after inserted variable
                                  setTimeout(() => {
                                    textarea.focus();
                                    textarea.selectionStart = startPos + variable.length;
                                    textarea.selectionEnd = startPos + variable.length;
                                  }, 0);
                                } else {
                                  // Fallback if textarea ref not available
                                  setEmailData(prev => ({
                                    ...prev,
                                    message: prev.message + variable
                                  }));
                                }
                              }
                              
                              // Reset dropdown selection
                              e.target.value = '';
                            }}
                            value=""
                            disabled={htmlMode}
                          >
                            <option value="" disabled>Choose variable...</option>
                            <option value="lead_name">{`{{lead_name}}`}</option>
                            <option value="first_name">{`{{first_name}}`}</option>
                            <option value="lead_email">{`{{lead_email}}`}</option>
                            <option value="user_name">{`{{user_name}}`}</option>
                            <option value="company_name">{`{{company_name}}`}</option>
                            <option value="country">{`{{country}}`}</option>
                          </select>
                        </div>
                      </div>

                      {/* Attachments section */}
                      <div className="flex items-center">
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileChange}
                          className="hidden"
                          multiple
                        />
                        <Button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          variant="outline"
                          className="px-3 py-1 text-xs"
                          disabled={emailData.loading || emailData.success}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                          Attach Files
                        </Button>
                        <span className="ml-2 text-xs text-gray-500">
                          {attachments.length > 0 ? `${attachments.length} file(s)` : ''}
                        </span>
                      </div>
                    </div>
                    
                    <div className="mt-1 mb-2 text-xs text-gray-500">
                      Use "Rich Text" for manual editing, "Email Preview" to see full HTML email templates (AI-generated), or "HTML Code" to edit raw HTML directly.
                    </div>
                  </div>
                  
                  {/* Attachments List Section - Only show when files are attached */}
                  {attachments.length > 0 && (
                    <div className="mb-4">
                      <div className="border border-gray-200 rounded-md p-2 bg-gray-50">
                        <h4 className="text-xs font-medium text-gray-700 mb-1">Attached Files</h4>
                        <ul className="space-y-1">
                          {attachments.map((attachment, index) => (
                            <li key={index} className="flex items-center justify-between py-1 px-2 bg-white rounded-md shadow-sm">
                              <div className="flex items-center">
                                {/* Show image preview for image files */}
                                {attachment.preview ? (
                                  <img src={attachment.preview} alt="Preview" className="h-8 w-8 object-cover rounded mr-2" />
                                ) : (
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                )}
                                <div>
                                  <div className="text-xs font-medium text-gray-900 truncate max-w-[150px]">
                                    {attachment.name}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {formatFileSize(attachment.size)}
                                  </div>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveAttachment(index)}
                                className="ml-2 text-red-500 hover:text-red-700 focus:outline-none"
                                disabled={emailData.loading || emailData.success}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {selectedTemplateId && (
                    <div className="mb-4 bg-gray-50 rounded-md border border-gray-200 overflow-hidden">
                      <button 
                        type="button"
                        onClick={() => setTemplateVarsCollapsed(!templateVarsCollapsed)}
                        className="w-full p-2 text-left flex items-center justify-between focus:outline-none"
                      >
                        <div className="flex items-center">
                          <h4 className="text-xs font-medium text-gray-700">Template Variables</h4>
                          {templates.find(t => t.id === parseInt(selectedTemplateId))?.variables?.length > 0 && (
                            <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                              {templates.find(t => t.id === parseInt(selectedTemplateId))?.variables?.length || 0}
                            </span>
                          )}
                        </div>
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${templateVarsCollapsed ? '' : 'transform rotate-180'}`} 
                          viewBox="0 0 20 20" 
                          fill="currentColor"
                        >
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                      
                      <div className={`px-2 pb-2 transition-all duration-200 ease-in-out ${templateVarsCollapsed ? 'max-h-0 overflow-hidden opacity-0' : 'max-h-40 overflow-y-auto opacity-100'}`}>
                        <div className="flex flex-wrap gap-1 text-xs">
                          {templates.find(t => t.id === parseInt(selectedTemplateId))?.variables?.map((variable, index) => (
                            <span key={index} className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded">
                              {`{{${variable}}}`}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Scheduling Section */}
                  <div className="mt-4 p-4 bg-gray-50 rounded-md border">
                    <div className="flex items-center space-x-3 mb-3">
                      <input
                        type="checkbox"
                        id="scheduleEmail"
                        checked={scheduleEmail}
                        onChange={(e) => {
                          setScheduleEmail(e.target.checked);
                          if (e.target.checked) {
                            // Set default to 1 hour from now
                            const defaultTime = new Date();
                            defaultTime.setHours(defaultTime.getHours() + 1);
                            setScheduledDateTime(defaultTime);
                          }
                        }}
                        disabled={emailData.loading || emailData.success}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="scheduleEmail" className="text-sm font-medium text-gray-700">
                        Schedule for later
                      </label>
                    </div>
                    
                    {scheduleEmail && (
                      <div className="animate-fadeIn">
                        <label htmlFor="scheduledDateTime" className="block text-sm font-medium text-gray-700 mb-2">
                          Send on
                        </label>
                        <DatePicker
                          selected={scheduledDateTime}
                          onChange={(date) => setScheduledDateTime(date)}
                          showTimeSelect
                          timeFormat="HH:mm"
                          timeIntervals={15}
                          timeCaption="time"
                          dateFormat="MMMM d, yyyy h:mm aa"
                          minDate={new Date()}
                          disabled={emailData.loading || emailData.success}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholderText="Select date and time"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Email will be sent at the scheduled time in your local timezone
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <Button
                      type="submit"
                      variant="primary"
                      className="w-full sm:ml-3 sm:w-auto text-base sm:text-sm"
                      disabled={emailData.loading || emailData.success}
                      isLoading={emailData.loading}
                    >
                      {emailData.loading 
                        ? (scheduleEmail ? 'Scheduling...' : 'Sending...') 
                        : emailData.success 
                          ? (scheduleEmail ? 'Scheduled!' : 'Sent!') 
                          : (scheduleEmail ? 'Schedule Email' : 'Send Email')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="mt-3 w-full sm:mt-0 sm:w-auto text-base sm:text-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSafeClose();
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Link Modal - leaving buttons as is for now */}
      <LinkModal
        isOpen={showLinkModal}
        onClose={closeLinkModal}
        linkData={linkData}
        onLinkDataChange={handleLinkDataChange}
        onInsert={insertLink}
      />

      {/* Confirmation modal for unsaved changes */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={() => {
          setShowConfirmModal(false);
          onClose();
        }}
        title="Unsaved Changes"
        message="You have unsaved changes in your email. If you close this window, your message will be lost."
        confirmButtonText="Discard Changes"
        cancelButtonText="Continue Editing"
        confirmButtonClass="bg-red-600 hover:bg-red-700 focus:ring-red-500"
        iconComponent={
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        }
      />

      {/* AI Email Generator Modal */}
      {showAiGenerator && (
        <div className="fixed inset-0 overflow-y-auto" style={{zIndex: 110}} role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowAiGenerator(false)}></div>
            <div className="inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-w-2xl">
              <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Generate Email Content with AI</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email Topic *</label>
                        <input
                          type="text"
                          value={aiPromptData.topic}
                          onChange={(e) => setAiPromptData({...aiPromptData, topic: e.target.value})}
                          placeholder="e.g., Product launch announcement, Follow-up after meeting"
                          className="w-full border border-gray-300 rounded px-3 py-2"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience *</label>
                        <input
                          type="text"
                          value={aiPromptData.audience}
                          onChange={(e) => setAiPromptData({...aiPromptData, audience: e.target.value})}
                          placeholder="e.g., Potential customers, Existing clients, Business partners"
                          className="w-full border border-gray-300 rounded px-3 py-2"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email Goal *</label>
                        <input
                          type="text"
                          value={aiPromptData.goal}
                          onChange={(e) => setAiPromptData({...aiPromptData, goal: e.target.value})}
                          placeholder="e.g., Schedule a meeting, Introduce new service, Get feedback"
                          className="w-full border border-gray-300 rounded px-3 py-2"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Tone</label>
                          <select
                            value={aiPromptData.tone}
                            onChange={(e) => setAiPromptData({...aiPromptData, tone: e.target.value})}
                            className="w-full border border-gray-300 rounded px-3 py-2"
                          >
                            <option value="professional">Professional</option>
                            <option value="friendly">Friendly</option>
                            <option value="formal">Formal</option>
                            <option value="casual">Casual</option>
                            <option value="persuasive">Persuasive</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                          <select
                            value={aiPromptData.language}
                            onChange={(e) => setAiPromptData({...aiPromptData, language: e.target.value})}
                            className="w-full border border-gray-300 rounded px-3 py-2"
                          >
                            <option value="english">English</option>
                            <option value="norwegian">Norwegian</option>
                            <option value="swedish">Swedish</option>
                            <option value="danish">Danish</option>
                            <option value="german">German</option>
                          </select>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Additional Information</label>
                        <textarea
                          value={aiPromptData.additionalInfo}
                          onChange={(e) => setAiPromptData({...aiPromptData, additionalInfo: e.target.value})}
                          placeholder="Any specific details, context, or requirements for the email..."
                          className="w-full border border-gray-300 rounded px-3 py-2 h-24"
                        />
                      </div>
                      
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="includeCompanyInfo"
                          checked={includeCompanyInfo}
                          onChange={(e) => setIncludeCompanyInfo(e.target.checked)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label htmlFor="includeCompanyInfo" className="ml-2 text-sm text-gray-700">
                          Include company information
                        </label>
                      </div>
                    </div>
                    
                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                      <Button
                        onClick={generateEmailWithAI}
                        variant="primary"
                        className="w-full sm:ml-3 sm:w-auto"
                        disabled={aiGenerating || !aiPromptData.topic || !aiPromptData.audience || !aiPromptData.goal}
                        isLoading={aiGenerating}
                      >
                        {aiGenerating ? 'Generating...' : 'Generate Email'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="mt-3 w-full sm:mt-0 sm:w-auto"
                        onClick={() => setShowAiGenerator(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Email Refiner Modal */}
      {showAiRefiner && emailData.message && (
        <div className="fixed inset-0 overflow-y-auto" style={{zIndex: 110}} role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowAiRefiner(false)}></div>
            <div className="inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-w-2xl">
              <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Refine Email Content with AI</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Refinement Instructions *</label>
                        <textarea
                          value={aiRefinementInstructions}
                          onChange={(e) => setAiRefinementInstructions(e.target.value)}
                          placeholder="Describe how you want to improve the email content..."
                          className="w-full border border-gray-300 rounded px-3 py-2 h-24"
                          required
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <div className="text-sm font-medium text-gray-700 mb-2">Common Refinements</div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          'Make it shorter and more concise',
                          'Make it more formal',
                          'Make it more friendly and personal',
                          'Add a clear call to action',
                          'Improve the subject line',
                          'Make it more persuasive'
                        ].map((suggestion, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => setAiRefinementInstructions(suggestion)}
                            className="text-left text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded border"
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                      
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="includeCompanyInfoRefine"
                          checked={includeCompanyInfo}
                          onChange={(e) => setIncludeCompanyInfo(e.target.checked)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label htmlFor="includeCompanyInfoRefine" className="ml-2 text-sm text-gray-700">
                          Include company information
                        </label>
                      </div>
                    </div>
                    
                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                      <Button
                        onClick={refineEmailWithAI}
                        variant="primary"
                        className="w-full sm:ml-3 sm:w-auto"
                        disabled={aiRefining || !aiRefinementInstructions}
                        isLoading={aiRefining}
                      >
                        {aiRefining ? 'Refining...' : 'Refine Email'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="mt-3 w-full sm:mt-0 sm:w-auto"
                        onClick={() => setShowAiRefiner(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Loading Overlay - matches MailerView styling */}
      {(aiGenerating || aiRefining) && (
        <div className="fixed inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-blue-700 flex items-center justify-center" style={{zIndex: 120}}>
          <div className="text-center">
            <div className="text-white text-lg font-medium mb-3">
              {aiGenerating ? 'Generating Email Content with AI' : 'Refining Email Content with AI'}
            </div>
            <div className="text-indigo-200 text-sm mb-6">
              Creating your personalized email...
            </div>

            <div className="max-w-md bg-white/10 backdrop-blur-sm p-4 rounded-lg text-white text-sm">
              <div className="font-medium mb-1">AI Tip:</div>
              <div>
                {[
                  "More detailed prompts generally lead to better AI-generated emails.",
                  "Specify your target audience to tailor the message appropriately.",
                  "For longer emails, consider breaking them into logical sections.",
                  "Being specific about your goal helps the AI craft more effective copy.",
                  "The tone setting can dramatically change how your message feels.",
                  "Including additional context yields more relevant and personalized content.",
                  "Remember to review AI-generated content for brand voice consistency.",
                  "For non-English emails, specify the language in your prompt."
                ][Math.floor(Math.random() * 8)]}
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-8 w-64 h-2 bg-indigo-800 rounded-full overflow-hidden mx-auto">
              <div className="h-full bg-gradient-to-r from-blue-400 via-indigo-500 to-purple-500 progress-animation rounded-full"></div>
            </div>

            <div className="text-indigo-200 text-xs mt-2 mb-6">
              Approximate time remaining...
            </div>

            {/* Bouncing dots */}
            <div className="flex space-x-1 mt-2 justify-center">
              <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
              <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{animationDelay: '0.4s'}}></div>
            </div>
          </div>

          {/* Add the same progress animation styles */}
          <style jsx>{`
            .progress-animation {
              animation: progress 3s ease-in-out infinite;
            }
            
            @keyframes progress {
              0% { transform: translateX(-100%); }
              50% { transform: translateX(0%); }
              100% { transform: translateX(100%); }
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

// Link Modal Component (Original buttons)
const LinkModal = ({ isOpen, onClose, linkData, onLinkDataChange, onInsert }) => {
  if (!isOpen) return null;
  
  return createPortal(
    <div /* ... portal wrapper ... */ >
      <div /* ... overlay ... */ ></div>
      <div /* ... modal content container ... */ >
        <h3 /* ... title ... */ >Insert Link</h3>
        
        <form /* ... */ >
          {/* ... inputs ... */}
          
          <div className="flex justify-end space-x-2 mt-5">
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Insert Link
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body 
  );
};

export default EmailModal;